import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const CARRIER_COLORS = {
  brisanet: "#10B981",
  claro: "#EF4444",
  dnet: "#3B82F6",
  vtal: "#A855F7",
};

const tooltipStyle = {
  contentStyle: { background: "#020617", border: "1px solid #1E293B", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 11 },
  labelStyle: { color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 10 },
};

// Build merged data: each row = one time point with one value per carrier for the given metric
function mergeSeries(qosData, metric) {
  if (!qosData) return [];
  const ids = Object.keys(qosData);
  if (!ids.length) return [];
  const length = qosData[ids[0]].points.length;
  return Array.from({ length }, (_, i) => {
    const row = { t: i };
    for (const id of ids) row[id] = qosData[id].points[i][metric];
    return row;
  });
}

function MetricChart({ title, metric, unit, data, yDomain, threshold }) {
  return (
    <div className="border border-slate-800 bg-[#020617] p-4" data-testid={`qos-chart-${metric}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">{title}</div>
          <div className="font-display text-xl font-black tracking-tight text-slate-100">{metric.toUpperCase()}</div>
        </div>
        <div className="text-[10px] font-mono text-slate-500">{unit}</div>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
          <XAxis dataKey="t" stroke="#475569" fontSize={9} tick={false} />
          <YAxis stroke="#475569" fontSize={9} domain={yDomain || ["auto", "auto"]} />
          <Tooltip {...tooltipStyle} />
          {Object.keys(CARRIER_COLORS).map((cid) => (
            <Line
              key={cid}
              type="monotone"
              dataKey={cid}
              stroke={CARRIER_COLORS[cid]}
              strokeWidth={1.6}
              dot={false}
              isAnimationActive={false}
              name={cid.toUpperCase()}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function QosCharts() {
  const { tenant } = useTenant();
  const [qos, setQos] = useState(null);

  useEffect(() => {
    if (!tenant) return;
    const load = () => api.get(`/sdwan/qos?tenant=${tenant.id}`).then(({ data }) => setQos(data));
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [tenant]);

  const latencyData = mergeSeries(qos, "latency");
  const jitterData = mergeSeries(qos, "jitter");
  const lossData = mergeSeries(qos, "loss");

  const summary = qos
    ? Object.entries(qos).map(([id, s]) => {
        const last = s.points[s.points.length - 1];
        return { id, name: s.carrier, role: s.role, ...last };
      })
    : [];

  return (
    <div className="p-5 space-y-5" data-testid="qos-panel">
      {/* Per-carrier summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {summary.map((c) => (
          <div key={c.id} className="border border-slate-800 bg-[#020617] p-3" data-testid={`qos-summary-${c.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ background: CARRIER_COLORS[c.id] }} />
              <span className="font-display font-bold text-sm">{c.name}</span>
              <span className="ml-auto text-[9px] font-mono uppercase text-slate-500">{c.role}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[10px] font-mono">
              <div>
                <div className="text-slate-500">LAT</div>
                <div className="text-slate-100 font-bold">{c.latency}ms</div>
              </div>
              <div>
                <div className="text-slate-500">JIT</div>
                <div className="text-slate-100 font-bold">{c.jitter}ms</div>
              </div>
              <div>
                <div className="text-slate-500">LOSS</div>
                <div className={`font-bold ${c.loss > 0.7 ? "text-red-400" : c.loss > 0.4 ? "text-amber-400" : "text-slate-100"}`}>{c.loss}%</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MetricChart title="Round Trip" metric="latency" unit="ms · target ≤ 50ms" data={latencyData} yDomain={[10, 55]} />
        <MetricChart title="Variance" metric="jitter" unit="ms · target ≤ 10ms" data={jitterData} yDomain={[0, 12]} />
        <MetricChart title="Dropped" metric="loss" unit="% · target ≤ 1%" data={lossData} yDomain={[0, 1.2]} />
      </div>

      <div className="flex items-center gap-4 text-[10px] font-mono">
        {Object.entries(CARRIER_COLORS).map(([id, color]) => (
          <div key={id} className="flex items-center gap-1.5">
            <span className="w-2.5 h-0.5" style={{ background: color }} />
            <span className="text-slate-400 uppercase">{id}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
